<template>
  <div class="address-details">
    <div class="details">
      <div style="position:relative">
        <label class="section-label">{{ address_type }} Address Details</label>
        <a class="clear-input" v-on:click="clearAddressForm">
          Clear
          <i class="fa fa-times"></i>
        </a>
      </div>
      <div class="form-group">
        <GmapAutocomplete
          ref="address"
          id="map"
          class="form-control"
          :placeholder="`${ address_type } Street Address`"          
          :select-first-on-enter="true"
          @place_changed="getAddressData"
          @blur="confirmAddress"          
          v-on:no-results-found="noAddressData"
          v-on:error="noAddressData"
        ></GmapAutocomplete>
        <transition name="slide" v-if="!address_found && show_address_wrapper">
          <span
            class="error-message"
          >Unfortunately we could not automatically locate your address, please capture below.</span>
        </transition>
      </div>
      <div class="address-details" v-if="show_address_wrapper">
        <div class="form-group">
          <input
            type="text"
            class="form-control"
            v-model.lazy="address.building"
            name="building"
            placeholder="Block/Apartment"
          />
        </div>

        <div
          class="form-group"
          v-bind:class="{ 'has-errors': errors.has('combined_number_street') }"
        >
          <input
            type="text"
            v-validate="'required'"
            class="form-control"
            v-model.lazy="address.combined_number_street"
            name="combined_number_street"
            data-vv-as="Street Address"
            placeholder="Street Address"
          />
          <span class="error-message">{{ errors.first('combined_number_street') }}</span>
        </div>
        <div class="form-group" v-bind:class="{ 'has-errors': errors.has('area') }">
          <input
            type="text"
            v-validate="'required'"
            class="form-control"
            v-model.lazy="address.area"
            name="area"
            placeholder="Area"
          />
          <span class="error-message">{{ errors.first('area') }}</span>
        </div>

        <div class="form-group" v-bind:class="{ 'has-errors': errors.has('city') }">
          <input
            type="text"
            v-validate="'required'"
            class="form-control"
            v-model.lazy="address.city"
            name="city"
            placeholder="City"
          />
          <span class="error-message">{{ errors.first('city') }}</span>
        </div>

        <div class="form-group" v-bind:class="{ 'has-errors': errors.has('province') }">
          <v-select
            class="v-dropdown form-control"
            :value="address.province"
            placeholder="Province"
            :options="province_options"
            @input="setProvince"
            :clearable="false"
            v-validate="'required'"
            data-vv-value-path="mutableValue"
            data-vv-name="Province"
            :has-error="errors.has('province')"
          ></v-select>
          <span class="error-message">{{ errors.first('province') }}</span>
        </div>

        <div class="form-group" v-bind:class="{ 'has-errors': errors.has('code') }">
          <input
            type="text"
            v-validate="'required'"
            class="form-control"
            v-model.lazy="address.code"
            name="code"
            data-vv-as="Postal Code"
            placeholder="Postal Code"
          />
          <span class="error-message">{{ errors.first('code') }}</span>
        </div>

        <div class="form-group" v-if="get_instructions">
          <input
            type="text"
            name="instructions"
            class="form-control"
            v-model.lazy="address.instructions"
            :placeholder="`Special ${address_type} Instructions`"
          />
        </div>
      </div>
    </div>

    <div v-if="show_address_error">
      <transition name="slide">
        <span class="error-message">{{ error_message }}</span>
      </transition>
    </div>
  </div>
</template>

<script src="vue-google-maps.js"></script>

<script>
import _ from "lodash";
import { gmapApi } from "vue2-google-maps";
import AddressModel from "@/models/address.model";
import VueGoogleAutocomplete from "vue-google-autocomplete";

export default {
  name: "vue-address-details",
  props: ["details", "type", "getInstructions", "errorMessage", "showInputs"],
  components: {
    "vue-google-autocomplete": VueGoogleAutocomplete
  },
  computed: {},
  data: function() {
    return {
      address: new AddressModel(self.details),
      province_options: [],
      address_found: false,
      address_type: "",
      show_address_wrapper: false,
      show_address_error: false,
      get_instructions: false,
      error_message: "Address is required."
    };
  },
  mounted() {
    var self = this;
    if (self.getInstructions) self.get_instructions = self.getInstructions;
    if (!self.isEmpty(self.errorMessage))
      self.error_message = self.errorMessage;
    if (!self.isEmpty(self.type)) self.address_type = self.type;
    self.initProvinces();
  },
  watch: {
    address: {
      handler(value) {
        var self = this;
        self.address.validateAddress();
        self.$emit("updated:address", value);
        
      },
      deep: true
    }
  },
  methods: {
    setProvince(value) {
      var self = this;
      self.address.province = value.label;
      self.address.province_code = value.value;
      self.country = "South Africa";
      self.country_code = "ZA";
    },
    initProvinces() {
      var self = this;
      self.province_options = [
        {label: 'Eastern Cape', value: 2961 },
        {label: 'Free State', value: 2962 },
        {label: 'Gauteng', value: 2963 },
        {label: 'KwaZulu-Natal', value: 2964 },
        {label: 'Limpopo', value: 2965 },
        {label: 'Mpumalanga', value: 2966 },
        {label: 'North West', value: 2967 },
        {label: 'Northern Cape', value: 2968 },
        {label: 'Western Cape', value: 2969 }
      ];
    },
    clearAddressForm() {
      var self = this;
      self.address = new AddressModel();
      self.address_found = false;
      self.show_address_wrapper = false;
      self.show_address_error = false;
      self.$refs.address.clear();
    },
    getAddressData: function(address, place) {
      var self = this;         
      self.address_found = true;
      self.show_address_wrapper = true;
      self.address = new AddressModel(
        self.formatePlaceData(address.address_components)
      );
      self.$log.debug("Address Data", address);
    },
    isEmpty(data) {
      return (
        data == null ||
        typeof data == "undefined" ||
        Object.keys(data).length === 0
      );
    },
    formatePlaceData: function(place_components) {
      var self = this;
      self.show_address_error = false;
      self.$log.debug("Place Data", place_components);
      var address = new AddressModel();

      for (var i = 0; i < place_components.length; i++) {
        var type = place_components[i].types;

        if (type.indexOf("premise") != -1) {
          address.building = place_components[i].long_name;
        }
        if (type.indexOf("street_number") != -1) {
          address.street_number = place_components[i].long_name;
        }
        if (type.indexOf("route") != -1) {
          address.street = place_components[i].long_name;
        }
        if (type.indexOf("locality") != -1) {
          address.area = place_components[i].short_name;
        }
        if (type.indexOf("administrative_area_level_2") != -1) {
          address.city = place_components[i].short_name;
        }
        if (type.indexOf("administrative_area_level_1") != -1) {
          address.province = place_components[i].long_name;
          address.province_code = place_components[i].short_name;
        }
        if (type.indexOf("postal_code") != -1) {
          address.code = place_components[i].long_name;
        }
        if (type.indexOf("country") != -1) {
          address.country = place_components[i].long_name;
          address.country_code = place_components[i].short_name;
        }
      }

      address.combined_number_street = (
        address.street_number +
        " " +
        address.street
      ).trim();

      return address;
    },
    confirmAddress: function() {
      var self = this;
      if (!self.address_found) self.show_address_wrapper = true;
    },
    noAddressData: function() {
      var self = this;
      self.$log.debug("No Address Data");
      self.show_address_wrapper = true;
      self.address_found = false;
      self.show_address_error = false;
    }
  }
};
</script>
